// apps/web/src/pages/confirm-email-change/confirm-email-change-operations.ts —
// Quick-Task 260920-u3p.
//
// GraphQL operation constants for the /confirm-email-change/[token]
// public deeplink page. The page mounts, fires the mutation, then
// redirects to /admin/login regardless of outcome.
//
// Per Quick-Task 260920-u3p (CONTEXT.md BLOCKER-4): the confirmation
// surface is Mutation-only — no Query.confirmAdminEmailChangeToken.
// The deeplink page calls the mutation on mount, which sidesteps
// preflight CORS issues that a GET-shaped alternative would have
// needed.
//
// The token IS the credential. The mutation is PUBLIC — no JWT, no
// CSRF (the resolver verifies Argon2id PHC + sha256 lookup digest +
// single-use status flip). See apps/api/src/graphql/resolvers/admin-account.ts
// for the full threat model.

import { gql } from "graphql-request";

/**
 * Confirm an in-flight admin email change. The mutation:
 *   - validates the raw token (16..256 chars)
 *   - looks up PendingEmailChange by sha256(token)
 *   - verifies Argon2id PHC against the row's hash
 *   - flips PendingEmailChange.status to "confirmed" (single-use)
 *   - updates AdminUser.email to the new address
 *   - revokes ALL RefreshToken families for that user (force re-login)
 *   - emits the AdminEmailChanged audit row + the ATO-Defense mail
 *     to the OLD address
 *
 * Returns the freshly-applied user + restaurant so the page can
 * build the post-confirm redirect (admin subdomain from restaurant.slug).
 *
 * Error codes the web client maps via i18next:
 *   SELF_SERVICE_TOKEN_INVALID — bad digest, Argon2id mismatch, or
 *                                 already-confirmed/cancelled row.
 *                                 The page MUST NOT distinguish these
 *                                 (the resolver deliberately does not
 *                                 leak which case it was).
 *   MAGIC_LINK_EXPIRED         — emailChangeTokenExpiresAt <= now.
 */
export const CONFIRM_ADMIN_EMAIL_CHANGE_MUTATION = gql`
  mutation ConfirmAdminEmailChangePage($token: String!) {
    confirmAdminEmailChange(token: $token) {
      user {
        id
        email
        role
      }
      restaurant {
        id
        slug
        name
      }
    }
  }
`;
